import React from 'react';
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConversationPanel from '../enhancement/ConversationPanel';
import { SAGE, CREAM } from '../../theme';

/**
 * L3 — the conversation shell, anchored to the line that prompted it.
 *
 * Follows the interview's "one component, two shells" pattern (design §12):
 * ConversationPanel is the shared surface, this is just the modal around it.
 * Reusing it also means the visitor gets the same send/disable behaviour as
 * the rest of the app — the input is disabled while a reply is pending, which
 * is the "one question at a time" nicety from profile-share §6c without any
 * extra code.
 *
 * The anchor is shown in the title so the visitor can see what they're asking
 * about, and is sent with the question so an escalation reaches Max with its
 * context attached.
 */
const ShareAskModal = ({
  anchor, thread, onClose, onSend, isProcessing, mode, senderName = 'the sender',
}) => {
  const open = Boolean(anchor);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh', display: 'flex', flexDirection: 'column',
          bgcolor: CREAM[50], border: `2px solid ${SAGE[200]}`, borderRadius: 4,
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 1.5 }}
      >
        {/* The recipient must always see exactly what they're asking about —
            source label, their verbatim quote, then the claim on a left rule. */}
        <Box sx={{ minWidth: 0 }}>
          {anchor?.sourceLabel && (
            <Typography sx={{ fontSize: 11, color: SAGE[400], mb: 0.25 }}>
              {anchor.sourceLabel}
            </Typography>
          )}
          <Typography sx={{ fontSize: 14.5, fontWeight: 500, lineHeight: 1.5, color: SAGE[800] }}>
            “{anchor?.quote}”
          </Typography>
          {anchor?.claim && (
            <Typography
              sx={{
                fontSize: 12, color: SAGE[500], mt: 0.75, pl: 1.25,
                borderLeft: `2px solid ${SAGE[200]}`, lineHeight: 1.5,
              }}
            >
              {anchor.claim}
            </Typography>
          )}
          {mode !== 'live' && (
            <Typography sx={{ fontSize: 11.5, color: SAGE[400], mt: 0.75 }}>
              {senderName} is answering these himself — leave your question and
              he'll reply by email.
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0 }}>
        {open && (
          <ConversationPanel
            messages={thread}
            onSendMessage={onSend}
            isProcessing={isProcessing}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareAskModal;
